/**
 * Preview switches: let one browser see a feature that is already deployed
 * but still off for everyone else, so it can be checked against real
 * customer data before customers see it.
 *
 *   https://lernia.flexime.io/?preview=diagnostics   turn that preview on
 *   https://lernia.flexime.io/?preview=off           turn all previews off
 *
 * The choice is stored in this browser's localStorage only. The parameter is
 * removed from the address bar straight away, so a link copied afterwards —
 * say, one sent to a customer — doesn't switch the preview on for them.
 *
 * Only names in KNOWN_PREVIEWS are accepted. When a preview graduates to "on
 * for everyone", delete its name here together with the checks for it.
 */
export const KNOWN_PREVIEWS = ['diagnostics'] as const
export type PreviewName = (typeof KNOWN_PREVIEWS)[number]

const STORAGE_KEY = 'flexime.previews'
const PARAM = 'preview'

const isKnown = (name: unknown): name is PreviewName =>
  typeof name === 'string' && (KNOWN_PREVIEWS as readonly string[]).includes(name)

const read = (): PreviewName[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter(isKnown) : []
  } catch {
    // Storage unavailable or corrupt: previews simply stay off.
    return []
  }
}

const write = (names: PreviewName[]) => {
  try {
    if (names.length > 0) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(names))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable: nothing to persist.
  }
}

export function isPreviewEnabled(name: PreviewName): boolean {
  return read().includes(name)
}

export function activePreviews(): PreviewName[] {
  return read()
}

/**
 * Apply `?preview=…` from the current URL, then strip it from the address
 * bar. Call once at startup, before the router mounts.
 */
export function applyPreviewParam(): void {
  let url: URL
  try {
    url = new URL(window.location.href)
  } catch {
    return
  }
  const value = url.searchParams.get(PARAM)
  if (value === null) return

  if (value === 'off') {
    write([])
  } else if (isKnown(value)) {
    const current = read()
    if (!current.includes(value)) write([...current, value])
  }
  // Unknown names are ignored — but still stripped from the URL.

  url.searchParams.delete(PARAM)
  try {
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  } catch {
    // Leaving the parameter in place is harmless; it has already been applied.
  }
}
